import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Panel } from "@/components/ui/Panel";
import { Metric } from "@/components/ui/Metric";
import { formatMoney } from "@bookboost/shared/format";

interface OverviewResponse {
  year: number;
  counts: { reps: number; deals: number; invoices: number; failedWebhooks: number };
  ytd: { earned: number; cash: number; equity: number; currency: string };
  outstanding: { total: number; aging30dPlus: number; currency: string };
  quality: { reversalRate: number; reversalCount: number; avgDaysToPaid: number | null };
  fxWarnings: string[];
}

export const Route = createFileRoute("/admin/")({
  component: AdminOverview,
});

function AdminOverview() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "overview"],
    queryFn: () => api.get<OverviewResponse>("/api/admin/overview"),
  });

  if (isLoading) {
    return (
      <div className="panel">
        <p className="lede">Loading overview…</p>
      </div>
    );
  }
  if (error || !data) {
    return (
      <Panel variant="err">
        <h2>Overview unavailable</h2>
        <p>{error instanceof Error ? error.message : "Failed to load admin overview."}</p>
      </Panel>
    );
  }

  return (
    <>
      <h1>Overview · {data.year}</h1>

      {data.fxWarnings.length > 0 && (
        <Panel variant="warn">
          <p>
            <strong>Missing FX rates:</strong> {data.fxWarnings.join(", ")}. Totals below exclude currencies without a
            rate. Add rates on the <a href="/admin/fx">FX</a> tab.
          </p>
        </Panel>
      )}

      <div className="grid cols-4">
        <Metric
          label="Earned YTD"
          value={formatMoney(data.ytd.earned, data.ytd.currency)}
          sub={`${data.counts.reps} active reps`}
          hint="Sum of every non-reversed commission earned this calendar year, FX-converted to EUR."
        />
        <Metric
          label="Cash YTD"
          value={formatMoney(data.ytd.cash, data.ytd.currency)}
          sub={`${formatMoney(data.ytd.equity, data.ytd.currency)} equity`}
          hint="Cash portion of YTD commissions. Equity portion shown beneath."
        />
        <Metric
          label="Outstanding"
          value={formatMoney(data.outstanding.total, data.outstanding.currency)}
          sub={`${formatMoney(data.outstanding.aging30dPlus, data.outstanding.currency)} aged 30d+`}
          tone="accent"
          hint="Earned + approved commissions not yet paid out."
        />
        <Metric
          label="Failed webhooks"
          value={String(data.counts.failedWebhooks)}
          sub={`${data.counts.deals} deals · ${data.counts.invoices} invoices`}
          tone={data.counts.failedWebhooks > 0 ? "err" : "default"}
          hint="Webhook deliveries from Stripe/HubSpot that errored on processing. Check the Webhooks tab."
        />
      </div>

      <div className="grid cols-2">
        <Metric
          label="Reversal rate"
          value={`${data.quality.reversalRate.toFixed(1)}%`}
          sub={`${data.quality.reversalCount} reversed YTD`}
          tone={data.quality.reversalRate > 5 ? "err" : "default"}
          hint="Share of commissions written this year that were later reversed. Sustained spikes here mean a broken structure or upstream data."
        />
        <Metric
          label="Avg days to paid"
          value={data.quality.avgDaysToPaid != null ? data.quality.avgDaysToPaid.toFixed(1) : "—"}
          sub="earned → paid"
          hint="Mean number of days between a commission being earned and being marked paid by finance. YTD."
        />
      </div>
    </>
  );
}
